import { useEffect, useRef, useState } from 'react';
import { apiRequest, ApiError } from '../lib/api';
import { downscalePanorama, fileSize, isEquirectFrame, NotEquirectangular, videoShape } from '../lib/downscale';
import { Alert, Button, Card } from './ui';
import { AuthedImage } from './AuthedImage';
import { AuthedVideo } from './AuthedVideo';
import { Video360, type VideoChapter } from './Video360';

/**
 * The landlord's side of the 360 tour and the walkthrough video.
 *
 * Built around COVERAGE rather than around a list of files. The screen's job
 * is to answer "which rooms have I still not shown?", so every section of the
 * catalogue is drawn whether or not it has a capture, the expected ones are
 * marked, and the progress line counts rooms rather than megabytes. A landlord
 * who has shot four rooms should be able to see the fifth is missing without
 * counting thumbnails.
 */

export interface TourSection {
  code: string; label: string; expected: boolean;
  covered: boolean; panoramaId: string | null;
}
export interface TourCoverage {
  sections: TourSection[];
  covered: number; expectedCovered: number; expectedTotal: number;
  missing: { code: string; label: string }[];
  complete: boolean;
}
export interface ListingVideo {
  id: string; mime_type: string; size_bytes: string | number;
  original_name: string | null; duration_seconds: number | null;
  /** 'equirect' for a 360 film (migration 165); older rows read as flat. */
  projection?: 'flat' | 'equirect' | string;
  chapters?: VideoChapter[] | null;
}

const MB = 1024 * 1024;

function duration(seconds: number | null): string | null {
  if (!seconds) return null;
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

/**
 * WHERE THIS EDITOR'S ENDPOINTS LIVE.
 *
 * A rental listing and a property for sale keep media in parallel tables behind
 * parallel routes, and the editor is the same editor: same sections, same one
 * video, same ticketed playback. Only the six URLs differ, so they are a prop
 * and the default is the rental set — every existing caller is untouched.
 */
export interface TourEndpoints {
  panoramas: (parentId: string) => string;
  panorama: (parentId: string, panoramaId: string) => string;
  panoramaContent: (panoramaId: string) => string;
  video: (parentId: string) => string;
  videoTicket: (videoId: string) => string;
  videoMedia: (videoId: string) => string;
}

export const RENTAL_TOUR: TourEndpoints = {
  panoramas: (id) => `/api/v1/listings/${id}/panoramas`,
  panorama: (id, pid) => `/api/v1/listings/${id}/panoramas/${pid}`,
  panoramaContent: (pid) => `/api/v1/listings/photos/${pid}/content`,
  video: (id) => `/api/v1/listings/${id}/video`,
  videoTicket: (vid) => `/api/v1/listings/videos/${vid}/ticket`,
  videoMedia: (vid) => `/api/v1/media/videos/${vid}`,
};

export const SALE_TOUR: TourEndpoints = {
  panoramas: (id) => `/api/v1/sale-listings/${id}/panoramas`,
  panorama: (id, pid) => `/api/v1/sale-listings/${id}/panoramas/${pid}`,
  panoramaContent: (pid) => `/api/v1/sale-listings/photos/${pid}/content`,
  video: (id) => `/api/v1/sale-listings/${id}/video`,
  videoTicket: (vid) => `/api/v1/sale-listings/videos/${vid}/ticket`,
  videoMedia: (vid) => `/api/v1/media/sale-videos/${vid}`,
};

export function ListingTourEditor({
  listingId, editable, tour, video, maxVideoBytes, onChange, urls = RENTAL_TOUR,
}: {
  listingId: string;
  editable: boolean;
  tour: TourCoverage;
  video: ListingVideo | null;
  maxVideoBytes: number;
  onChange: () => void;
  urls?: TourEndpoints;
}) {
  const [busy, setBusy] = useState<string | null>(null);
  const [tourMsg, setTourMsg] = useState<{ tone: 'danger' | 'success'; text: string } | null>(null);
  const [videoMsg, setVideoMsg] = useState<{ tone: 'danger' | 'success'; text: string } | null>(null);
  const [progress, setProgress] = useState<number | null>(null);
  const [want360, setWant360] = useState(false);
  const panoInput = useRef<HTMLInputElement>(null);
  const videoInput = useRef<HTMLInputElement>(null);
  const pending = useRef<string | null>(null);

  const limitMb = Math.round(maxVideoBytes / MB);

  const pickPanorama = (section: string) => {
    pending.current = section;
    panoInput.current?.click();
  };

  const onPanorama = async (files: FileList | null) => {
    const section = pending.current;
    if (!files?.length || !section) return;
    setBusy(section); setTourMsg(null);
    try {
      const small = await downscalePanorama(files[0]!);
      await apiRequest(urls.panoramas(listingId), {
        method: 'POST',
        body: { mimeType: small.mimeType, contentBase64: small.contentBase64, section },
      });
      setTourMsg({ tone: 'success',
        text: `360° view added — ${fileSize(files[0]!.size)} prepared as ${fileSize(small.bytes)}.` });
      onChange();
    } catch (e) {
      // The "that is not a 360 photo" case carries its own explanation, and it
      // is the one a landlord will actually hit.
      setTourMsg({ tone: 'danger', text: e instanceof NotEquirectangular || e instanceof ApiError
        ? e.message : 'That 360° view could not be added.' });
    } finally {
      setBusy(null); pending.current = null;
      if (panoInput.current) panoInput.current.value = '';
    }
  };

  const removePanorama = async (panoramaId: string, section: string) => {
    setBusy(section); setTourMsg(null);
    try {
      await apiRequest(urls.panorama(listingId, panoramaId), { method: 'DELETE' });
      onChange();
    } catch (e) {
      setTourMsg({ tone: 'danger',
        text: e instanceof ApiError ? e.message : 'That 360° view could not be removed.' });
    } finally { setBusy(null); }
  };

  /**
   * The video goes up as the raw request body, not as base64 in JSON.
   *
   * XMLHttpRequest rather than fetch, purely for `upload.onprogress`: an 80MB
   * file on a Kenyan connection is minutes of waiting, and a spinner with no
   * percentage is indistinguishable from a hang. fetch() still cannot report
   * upload progress in any browser we support.
   */
  const onVideo = async (files: FileList | null) => {
    const file = files?.[0];
    if (!file) return;
    setVideoMsg(null);

    if (file.size > maxVideoBytes) {
      setVideoMsg({ tone: 'danger', text: `That video is ${fileSize(file.size)}. The limit is ${limitMb}MB. `
        + 'Try trimming it, or export it at a lower resolution.' });
      if (videoInput.current) videoInput.current.value = '';
      return;
    }
    if (file.type !== 'video/mp4' && file.type !== 'video/webm') {
      setVideoMsg({ tone: 'danger', text: 'Upload an MP4 or WebM video. Most phones record MP4 already.' });
      if (videoInput.current) videoInput.current.value = '';
      return;
    }

    setBusy('video'); setProgress(0);
    const shape = await videoShape(file);
    const secs = shape.seconds;
    // A 2:1 frame is a 360 film whatever the box says; a film that is not 2:1
    // cannot be one, and showing it on a sphere would bend every wall.
    const twoToOne = isEquirectFrame(shape.width, shape.height);
    if (want360 && shape.width && !twoToOne) {
      setBusy(null); setProgress(null);
      setVideoMsg({ tone: 'danger', text: `That video is ${shape.width}x${shape.height}. A 360 video is exactly `
        + 'twice as wide as it is tall (for example 3840x1920). Export it from the camera app as an '
        + '"equirectangular" or "360" video, or untick the 360 box to upload it as an ordinary walkthrough.' });
      if (videoInput.current) videoInput.current.value = '';
      return;
    }
    const is360 = want360 || twoToOne;

    try {
      await new Promise<void>((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        const q = new URLSearchParams();
        if (secs) q.set('duration', String(secs));
        if (is360) q.set('projection', 'equirect');
        const url = `${urls.video(listingId)}${q.size ? `?${q}` : ''}`;
        xhr.open('POST', url);
        xhr.setRequestHeader('content-type', file.type);
        xhr.setRequestHeader('x-file-name', encodeURIComponent(file.name).slice(0, 200));
        const session = localStorage.getItem('pms.session');
        if (session) {
          try {
            xhr.setRequestHeader('authorization', `Bearer ${JSON.parse(session).accessToken}`);
          } catch { /* a corrupt session is handled by the 401 below */ }
        }
        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) setProgress(Math.round((e.loaded / e.total) * 100));
        };
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) return resolve();
          let message = 'That video could not be uploaded.';
          // RFC 9457 problem details: the sentence for a person is `detail`.
          try { message = JSON.parse(xhr.responseText).detail || message; } catch { /* keep the default */ }
          reject(new Error(message));
        };
        xhr.onerror = () => reject(new Error('The upload was interrupted. Check your connection and try again.'));
        xhr.send(file);
      });
      setVideoMsg({ tone: 'success',
        text: `${is360 ? '360° video' : 'Video'} uploaded, ${fileSize(file.size)}${secs ? `, ${duration(secs)}` : ''}.`
          + (is360 ? ' Now mark where each room starts, below the player.' : '') });
      onChange();
    } catch (e) {
      setVideoMsg({ tone: 'danger',
        text: e instanceof Error ? e.message : 'That video could not be uploaded.' });
    } finally {
      setBusy(null); setProgress(null);
      if (videoInput.current) videoInput.current.value = '';
    }
  };

  const removeVideo = async () => {
    setBusy('video'); setVideoMsg(null);
    try {
      await apiRequest(urls.video(listingId), { method: 'DELETE' });
      onChange();
    } catch (e) {
      setVideoMsg({ tone: 'danger',
        text: e instanceof ApiError ? e.message : 'That video could not be removed.' });
    } finally { setBusy(null); }
  };

  const pct = Math.round((tour.expectedCovered / Math.max(1, tour.expectedTotal)) * 100);

  return (
    <>
      <Card title="360° tour">
        <p className="page-sub" style={{ marginTop: 0 }}>
          A 360° photo lets someone stand in the room and look around, which is the
          question a renter actually has. Shoot one per room with a 360 camera, or with
          your phone’s panorama mode.
        </p>

        {tourMsg && <Alert tone={tourMsg.tone}>{tourMsg.text}</Alert>}

        <div className="lf-tour-bar" aria-hidden>
          <div className="lf-tour-fill" style={{ width: `${pct}%` }} />
        </div>
        <p className="lf-tour-count" role="status">
          {tour.complete
            ? `Every main room is covered — ${tour.covered} view${tour.covered === 1 ? '' : 's'} in this tour.`
            : `${tour.expectedCovered} of ${tour.expectedTotal} main rooms covered. `
              + `Still to shoot: ${tour.missing.map((m) => m.label.toLowerCase()).join(', ')}.`}
        </p>

        <ul className="lf-tour-grid">
          {tour.sections.map((s) => (
            <li key={s.code} className={`lf-tour-item${s.covered ? ' on' : ''}`}>
              {s.covered && s.panoramaId ? (
                <AuthedImage src={urls.panoramaContent(s.panoramaId)} alt="" loading="lazy" />
              ) : (
                <div className="lf-tour-empty" aria-hidden>360°</div>
              )}
              <div className="lf-tour-meta">
                <span className="lf-tour-label">
                  {s.label}
                  {s.expected && !s.covered && <em className="lf-tour-needed"> · needed</em>}
                </span>
                {editable && (
                  s.covered && s.panoramaId
                    ? <button type="button" className="btn btn-link btn-sm"
                        disabled={busy === s.code} onClick={() => removePanorama(s.panoramaId!, s.code)}>
                        {busy === s.code ? 'Removing…' : 'Remove'}
                      </button>
                    : <button type="button" className="btn btn-link btn-sm"
                        disabled={busy === s.code} onClick={() => pickPanorama(s.code)}>
                        {busy === s.code ? 'Adding…' : 'Add 360°'}
                      </button>
                )}
              </div>
            </li>
          ))}
        </ul>

        <input ref={panoInput} type="file" accept="image/jpeg,image/webp,image/png" hidden
          onChange={(e) => onPanorama(e.target.files)} />
        <p className="page-sub" style={{ marginTop: 8 }}>
          A 360 photo is twice as wide as it is tall. Anything else is refused here rather
          than uploaded and shown warped.
        </p>
      </Card>

      <Card title="Walkthrough video">
        {video ? (
          <>
            {/* A ticket rather than a blob: an 80MB blob must download whole
                before the first frame and cannot be seeked. */}
            {video.projection === 'equirect' ? (
              <VideoDetails360 key={video.id} listingId={listingId} video={video} editable={editable}
                sections={tour.sections} urls={urls} onSaved={onChange} />
            ) : (
              <AuthedVideo className="lf-video"
                ticketUrl={urls.videoTicket(video.id)}
                mediaUrl={urls.videoMedia(video.id)} />
            )}
            <p className="page-sub">
              {video.original_name ? decodeURIComponent(video.original_name) : 'Walkthrough'}
              {' · '}{fileSize(Number(video.size_bytes))}
              {video.duration_seconds ? ` · ${duration(video.duration_seconds)}` : ''}
            </p>
            {editable && (
              <div className="lf-video-actions">
                <Button variant="secondary" type="button" onClick={removeVideo} loading={busy === 'video'}>
                  Remove video
                </Button>
                <ProjectionSwitch listingId={listingId} video={video} urls={urls} onSaved={onChange}
                  onError={(text) => setVideoMsg({ tone: 'danger', text })} />
              </div>
            )}
          </>
        ) : (
          <>
            <p className="page-sub" style={{ marginTop: 0 }}>
              One short walkthrough, up to {limitMb}MB. A minute of walking through the
              front door, the living room and the kitchen does more than any description.
            </p>
            {editable && (
              <>
                <label className="lf-360-toggle">
                  <input type="checkbox" checked={want360} onChange={(e) => setWant360(e.target.checked)} />
                  <span>
                    <strong>This is a 360° video</strong>
                    <small>Shot on a 360 camera (Insta360, Ricoh Theta, GoPro Max) and exported as one 2:1
                      film. Renters can look around every room while it plays. A 2:1 file is recognised
                      as 360 even if this is left unticked.</small>
                  </span>
                </label>
                <Button type="button" variant="secondary" loading={busy === 'video'}
                  onClick={() => videoInput.current?.click()}>
                  {want360 ? 'Choose the 360° video' : 'Choose a video'}
                </Button>
                <input ref={videoInput} type="file" accept="video/mp4,video/webm" hidden
                  onChange={(e) => onVideo(e.target.files)} />
              </>
            )}
            {progress !== null && (
              <div className="lf-upload">
                <div className="lf-tour-bar" aria-hidden>
                  <div className="lf-tour-fill" style={{ width: `${progress}%` }} />
                </div>
                <p className="page-sub" role="status">Uploading… {progress}%</p>
              </div>
            )}
          </>
        )}
        {videoMsg && <Alert tone={videoMsg.tone}>{videoMsg.text}</Alert>}
      </Card>
    </>
  );
}

/**
 * Fixes the one mistake an auto-detected upload can make: a 2:1 film that is a
 * wide panorama and not a 360 one, or a 360 film exported at an odd size.
 */
function ProjectionSwitch({ listingId, video, urls, onSaved, onError }: {
  listingId: string; video: ListingVideo; urls: TourEndpoints;
  onSaved: () => void; onError: (text: string) => void;
}) {
  const [busy, setBusy] = useState(false);
  const is360 = video.projection === 'equirect';
  const flip = async () => {
    setBusy(true);
    try {
      await apiRequest(urls.video(listingId), {
        method: 'PATCH', body: { projection: is360 ? 'flat' : 'equirect' },
      });
      onSaved();
    } catch (e) {
      onError(e instanceof ApiError ? e.message : 'That change could not be saved.');
    } finally { setBusy(false); }
  };
  return (
    <Button variant="ghost" type="button" onClick={flip} loading={busy}>
      {is360 ? 'Show as an ordinary video' : 'Show as a 360° video'}
    </Button>
  );
}

const mmss = (s: number) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, '0')}`;
/** "1:05", "65" and "1.05" all mean sixty-five seconds; anything else is null. */
function parseClock(text: string): number | null {
  const t = text.trim();
  if (/^\d+$/.test(t)) return Number(t);
  const m = /^(\d+)[:.](\d{1,2})$/.exec(t);
  if (!m || Number(m[2]) > 59) return null;
  return Number(m[1]) * 60 + Number(m[2]);
}

interface Row { key: number; at: string; section: string; label: string }

/**
 * The 360 player with the room markers under it.
 *
 * Marking rooms is done while watching, not by typing times from memory: play
 * the film, and when the front door opens into the lounge press "Mark this
 * moment". The time goes into a new row with the next room on the tour list
 * already picked, so a seven-room film is seven taps and a save.
 */
function VideoDetails360({ listingId, video, editable, sections, urls, onSaved }: {
  listingId: string; video: ListingVideo; editable: boolean;
  sections: TourSection[]; urls: TourEndpoints; onSaved: () => void;
}) {
  const [src, setSrc] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const now = useRef(0);
  const nextKey = useRef(1);
  const toRows = (cs: VideoChapter[] | null | undefined): Row[] =>
    (cs ?? []).map((c) => ({ key: nextKey.current++, at: mmss(c.at), section: c.section,
      label: c.label === sections.find((s) => s.code === c.section)?.label ? '' : c.label }));
  const [rows, setRows] = useState<Row[]>(() => toRows(video.chapters));
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<{ tone: 'danger' | 'success'; text: string } | null>(null);

  useEffect(() => {
    let cancelled = false;
    apiRequest<{ token: string }>(urls.videoTicket(video.id))
      .then((r) => { if (!cancelled) setSrc(`${urls.videoMedia(video.id)}?t=${encodeURIComponent(r.token)}`); })
      .catch(() => { if (!cancelled) setFailed(true); });
    return () => { cancelled = true; };
  }, [urls, video.id]);

  // What the player shows: saved chapters, or the rows being edited once they parse.
  const preview: VideoChapter[] = rows.flatMap((r) => {
    const at = parseClock(r.at);
    const label = r.label.trim() || sections.find((s) => s.code === r.section)?.label || r.section;
    return at === null ? [] : [{ at, section: r.section, label }];
  });

  const used = new Set(rows.map((r) => r.section));
  const nextSection = () => sections.find((s) => s.covered && !used.has(s.code))?.code
    ?? sections.find((s) => !used.has(s.code))?.code ?? sections[0]?.code ?? 'living';

  const mark = () => {
    setMsg(null);
    const at = Math.floor(now.current);
    setRows((rs) => [...rs.filter((r) => parseClock(r.at) !== at),
      { key: nextKey.current++, at: mmss(at), section: nextSection(), label: '' }]
      .sort((a, b) => (parseClock(a.at) ?? 0) - (parseClock(b.at) ?? 0)));
  };
  const change = (key: number, patch: Partial<Row>) =>
    setRows((rs) => rs.map((r) => (r.key === key ? { ...r, ...patch } : r)));

  const save = async () => {
    setMsg(null);
    const bad = rows.find((r) => parseClock(r.at) === null);
    if (bad) { setMsg({ tone: 'danger', text: `"${bad.at}" is not a time. Write it as minutes:seconds, like 1:05.` }); return; }
    const chapters = rows
      .map((r) => ({ at: parseClock(r.at)!, section: r.section, ...(r.label.trim() ? { label: r.label.trim() } : {}) }))
      .sort((a, b) => a.at - b.at);
    for (let i = 1; i < chapters.length; i++) {
      if (chapters[i]!.at === chapters[i - 1]!.at) {
        setMsg({ tone: 'danger', text: `Two rooms start at ${mmss(chapters[i]!.at)}. Give each its own moment.` });
        return;
      }
    }
    setSaving(true);
    try {
      await apiRequest(urls.video(listingId), { method: 'PATCH', body: { chapters } });
      setMsg({ tone: 'success', text: chapters.length
        ? `Saved. Renters can now jump straight to any of the ${chapters.length} rooms.`
        : 'Saved. The film plays through with no room markers.' });
      onSaved();
    } catch (e) {
      setMsg({ tone: 'danger', text: e instanceof ApiError ? e.message : 'The rooms could not be saved.' });
    } finally { setSaving(false); }
  };

  if (failed) return <p className="muted">This video could not be loaded. Reload the page to try again.</p>;
  if (!src) return <p className="muted">Loading the video…</p>;

  return (
    <div className="lf-360">
      <Video360 src={src} chapters={preview} title="this property" onTime={(t) => { now.current = t; }} />
      {editable && (
        <div className="lf-chapters">
          <div className="lf-chapters-head">
            <div>
              <strong>Rooms in this video</strong>
              <p className="page-sub">Play the film and press <em>Mark this moment</em> as each room comes
                into view. Renters get a button per room and a marker on the timeline.</p>
            </div>
            <Button type="button" variant="secondary" onClick={mark}>Mark this moment</Button>
          </div>
          {rows.length > 0 && (
            <ul className="lf-chapter-list">
              {rows.map((r) => (
                <li key={r.key} className="lf-chapter">
                  <input className="input lf-chapter-at num" aria-label="Starts at (minutes:seconds)"
                    value={r.at} inputMode="numeric" onChange={(e) => change(r.key, { at: e.target.value })} />
                  <select className="input" aria-label="Room" value={r.section}
                    onChange={(e) => change(r.key, { section: e.target.value })}>
                    {sections.map((s) => <option key={s.code} value={s.code}>{s.label}</option>)}
                  </select>
                  <input className="input" aria-label="Name shown to renters (optional)" maxLength={60}
                    placeholder={sections.find((s) => s.code === r.section)?.label ?? 'Name (optional)'}
                    value={r.label} onChange={(e) => change(r.key, { label: e.target.value })} />
                  <button type="button" className="btn btn-link btn-sm"
                    onClick={() => setRows((rs) => rs.filter((x) => x.key !== r.key))}
                    aria-label={`Remove the marker at ${r.at}`}>Remove</button>
                </li>
              ))}
            </ul>
          )}
          <div className="lf-chapters-foot">
            <Button type="button" onClick={save} loading={saving}>Save rooms</Button>
            <span className="muted">{rows.length} of 24 markers</span>
          </div>
          {msg && <Alert tone={msg.tone}>{msg.text}</Alert>}
        </div>
      )}
    </div>
  );
}
